class FaqOverzicht extends HTMLElement {
  connectedCallback() {
    this.groepen = [...this.querySelectorAll('[data-faq-groep]')];
    this.links = [...this.querySelectorAll('[data-faq-naar]')];
    if (!this.groepen.length || !this.links.length) return;

    this.perId = new Map(this.links.map((a) => [a.dataset.faqNaar, a]));
    this.actief = null;
    this.gepland = false;

    this.opScroll = this.opScroll.bind(this);
    this.opKlik = this.opKlik.bind(this);
    this.opTouchStart = this.opTouchStart.bind(this);
    this.opTouchEnd = this.opTouchEnd.bind(this);

    // Alle listeners aan één signaal, en de scroll-context één keer
    // vastleggen. disconnectedCallback vroeg die context eerst opnieuw op, en
    // is die intussen veranderd (venster over de 750px-grens geschoven), dan
    // bleef de scroll-listener op de oude hangen, ook na het vervangen van de
    // sectie in de editor.
    this.afbreken = new AbortController();
    const { signal } = this.afbreken;
    this.scrollDoel = this.scrollContainer;

    // passive: deze handler roept nooit preventDefault aan, en dan mag de
    // browser doorscrollen zonder op ons te wachten.
    this.scrollDoel.addEventListener('scroll', this.opScroll, { passive: true, signal });
    addEventListener('resize', this.opScroll, { passive: true, signal });
    this.addEventListener('click', this.opKlik, { signal });

    // De navigatie is op mobiel een horizontaal scrollbare rij. Zodra de
    // browser een vinger tijdens een tik als vegen herkent, neemt hij de
    // aansturing van die aanraking zelf over voor het scrollen. Bij
    // Pointer Events (pointerdown/pointerup) betekent dat een pointercancel
    // in plaats van een pointerup: de tik-detectie vuurt dan nooit, ook niet
    // bij een tik die nauwelijks bewogen heeft. Ruwe Touch Events blijven
    // wel gewoon doorlopen zolang de vinger op het scherm staat, native
    // scroll of niet — vandaar touchstart/touchend hier in plaats van
    // pointerdown/pointerup. Getest: zonder deze fix deed een tik op een
    // categorie op een echt mobiel scherm niets, met wel.
    this.touchStart = null;
    this.addEventListener('touchstart', this.opTouchStart, { passive: true, signal });
    this.addEventListener('touchend', this.opTouchEnd, { signal });

    // Direct na het opzetten meten geeft op een eerste bezoek vaak nog het
    // verkeerde antwoord: lettertypes zijn dan soms nog niet ingeladen, en
    // tot dat gebeurt kunnen de posities van de groepen nog verschuiven.
    // markeer() concludeerde daardoor soms al bij het openen dat je onderaan
    // de pagina stond, markeerde dan de laatste categorie als actief, en
    // liet houdInBeeld() de navigatie daar meteen naartoe schuiven — leek
    // dan alsof de navigatie "startte" bij een willekeurige categorie in
    // plaats van de eerste. Vandaar twee tekenframes wachten (het ontwerp is
    // dan al minstens één keer opnieuw ingedeeld), plus nog eens meten
    // zodra alles écht klaar is, voor als een laat ladend lettertype de
    // hoogtes alsnog laat verschuiven.
    requestAnimationFrame(() => requestAnimationFrame(() => this.markeer()));
    if (document.readyState !== 'complete') {
      addEventListener('load', () => this.markeer(), { once: true });
    }

    // Binnenkomen met een #anker: de browser springt zelf, maar de markering
    // moet daarna kloppen.
    if (location.hash) requestAnimationFrame(() => this.markeer());
  }

  disconnectedCallback() {
    this.afbreken?.abort();
  }

  // De echte scroll-context van de pagina. Op desktop staan <html> en
  // <body> in dit thema op overflow: hidden en scrollt .page-wrapper zelf
  // (overflow: auto) — vandaar dat eerdere versie die altijd koos zodra hij
  // bestond. Op mobiel (onder de layout-breakpoint) draait het thema dat om:
  // daar staat .page-wrapper wel in de DOM maar zonder overflow, dus zijn
  // scrollHeight en clientHeight zijn er altijd gelijk aan elkaar en scrolt
  // hij zelf nooit — de pagina scrolt dan gewoon via het document, zoals bij
  // een thema zonder eigen scroll-wrapper. Blindelings voor .page-wrapper
  // kiezen las en stuurde daardoor op mobiel een element aan dat nooit
  // beweegt: markeer() zag scrollTop altijd op 0 en viewportHeight gelijk
  // aan contentHeight, concludeerde dus voortdurend "onderaan de pagina" en
  // markeerde de laatste categorie al bij het laden; gaNaar() scrollde om
  // dezelfde reden zichtbaar niets bij een tik op een link. Vandaar hier
  // testen of .page-wrapper ECHT meer inhoud heeft dan zijn eigen hoogte in
  // plaats van enkel of hij bestaat, met window als terugval voor elk thema
  // (of elke breedte) waar de pagina gewoon zelf scrolt.
  get scrollContainer() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
    return window;
  }

  get scrollTop() {
    const c = this.scrollContainer;
    return c === window ? scrollY : c.scrollTop;
  }

  get viewportHeight() {
    const c = this.scrollContainer;
    return c === window ? innerHeight : c.clientHeight;
  }

  get contentHeight() {
    const c = this.scrollContainer;
    return c === window ? document.body.scrollHeight : c.scrollHeight;
  }

  get offset() {
    // Uit de sectie-instelling, via de custom property op dit element. Onder
    // 750px geldt dezelfde media query-grens als in de CSS: de mobiele
    // header is lager, dus daar telt de mobiele offset, niet de
    // desktopwaarde. Blijven ze uit elkaar lopen, dan klopt de sprong bij
    // een klik niet met waar de sticky nav in werkelijkheid staat.
    const mobiel = matchMedia('(max-width: 749px)').matches;
    const naam = mobiel ? '--faq-offset-mobiel' : '--faq-offset';
    const fallback = mobiel ? 64 : 100;
    const v = parseInt(getComputedStyle(this).getPropertyValue(naam), 10);
    const basis = Number.isFinite(v) ? v : fallback;

    // Op desktop staat de nav los naast de inhoud: de header-offset alleen
    // volstaat. Op mobiel ligt de nav zelf ALS vastgeplakte balk boven de
    // inhoud, dus telt zijn eigen hoogte ook mee — anders scrollt een klik
    // op een link de titel voor de helft achter de nav, ook al klopt
    // scroll-margin-top in de CSS (die geldt alleen bij een directe
    // #anker-sprong, niet bij deze JS-scroll). Geen extra lucht meer
    // bovenop: de onderrand van de nav moet precies op het lijntje tussen
    // twee secties uitkomen, en de ademruimte tot de titel komt al uit de
    // eigen padding-top van de groep (.faq__groep). Nog een marge erbij zou
    // dat effect dubbel doen en een tweede, losse lijn geven. Zijn
    // werkelijke hoogte opmeten in plaats van een vaste waarde aannemen,
    // want die kan meebewegen met het lettertype of een langere
    // categorienaam.
    if (!mobiel) return basis;
    const nav = this.querySelector('.faq__nav');
    const navHoogte = nav ? nav.getBoundingClientRect().height : 56;
    return basis + navHoogte - 2;
  }

  opScroll() {
    // Eén meting per frame. Scroll vuurt veel vaker dan de browser tekent, en
    // elke meting hier leest layout, dus ongebreideld meten kost frames.
    if (this.gepland) return;
    this.gepland = true;
    requestAnimationFrame(() => {
      this.gepland = false;
      this.markeer();
    });
  }

  /* Bewust "de laatste groep die de lijn gepasseerd is" en geen
     IntersectionObserver. Bij groepen die veel hoger zijn dan het scherm, en
     bij een groep die opengeklapt ineens veel langer wordt, geeft een
     observer wisselende uitkomsten afhankelijk van de drempels. Deze regel is
     altijd te voorspellen: actief is de laatste kop die boven de leeslijn
     staat. */
  markeer() {
    const lijn = this.offset + 1;
    let huidig = this.groepen[0];

    for (const groep of this.groepen) {
      if (groep.getBoundingClientRect().top <= lijn) huidig = groep;
      else break;
    }

    // Onderaan de pagina komt de laatste groep vaak nooit meer boven de lijn.
    // Dan is de laatste altijd de actieve, anders blijft de markering hangen.
    const onderaan = this.viewportHeight + this.scrollTop >= this.contentHeight - 2;
    if (onderaan) huidig = this.groepen[this.groepen.length - 1];

    if (huidig === this.actief) return;
    this.actief = huidig;

    for (const a of this.links) a.removeAttribute('aria-current');
    const link = this.perId.get(huidig.id);
    if (link) {
      link.setAttribute('aria-current', 'true');
      this.houdInBeeld(link);
    }
  }

  // Op mobiel is de navigatie een horizontale rij. De actieve link moet dan
  // meeschuiven, anders wijst hij naar iets buiten beeld.
  houdInBeeld(link) {
    const lijst = link.closest('[data-faq-nav]');
    if (!lijst || lijst.scrollWidth <= lijst.clientWidth) return;
    const l = link.getBoundingClientRect();
    const r = lijst.getBoundingClientRect();
    if (l.left < r.left || l.right > r.right) {
      lijst.scrollBy({ left: l.left - r.left - 16, behavior: this.zacht });
    }
  }

  get zacht() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  opKlik(e) {
    const link = e.target.closest('[data-faq-naar]');
    if (!link) return;
    e.preventDefault();
    this.gaNaar(link);
  }

  opTouchStart(e) {
    const link = e.target.closest('[data-faq-naar]');
    const t = e.changedTouches[0];
    this.touchStart = link ? { x: t.clientX, y: t.clientY, link } : null;
  }

  opTouchEnd(e) {
    if (!this.touchStart) return;
    const { x, y, link } = this.touchStart;
    this.touchStart = null;

    // Een reistafstand van 10px is nog altijd een tik met een trillende
    // vinger, niet een bewuste veeg. Boven die grens laten we gewoon
    // scrollen en doen we zelf niets.
    const t = e.changedTouches[0];
    const afstand = Math.hypot(t.clientX - x, t.clientY - y);
    if (afstand > 10) return;

    // preventDefault op touchend onderdrukt ook het synthetische click-event
    // dat de browser er normaal nog achteraan stuurt, dus opKlik draait
    // hierna niet nog eens voor dezelfde tik.
    e.preventDefault();
    this.gaNaar(link);
  }

  gaNaar(link) {
    const doel = document.getElementById(link.dataset.faqNaar);
    if (!doel) return;

    const top = doel.getBoundingClientRect().top + this.scrollTop - this.offset;
    this.scrollContainer.scrollTo({ top, behavior: this.zacht });

    // De focus meenemen naar de kop van de groep. Door preventDefault doet
    // de browser dat niet meer zelf, en dan stond een toetsenbordgebruiker na
    // de sprong nog in de navigatie: de volgende Tab ging naar de volgende
    // categorie in plaats van naar de eerste vraag van deze groep.
    // preventScroll, want het scrollen hierboven doet het werk al.
    const kop = doel.querySelector('.faq__groep-titel') || doel;
    if (!kop.hasAttribute('tabindex')) kop.setAttribute('tabindex', '-1');
    kop.focus({ preventScroll: true });

    // De hash bijwerken zonder de sprong die een echte hash-navigatie doet,
    // zodat de link deelbaar blijft en de terugknop werkt.
    history.replaceState(null, '', '#' + link.dataset.faqNaar);
  }
}

if (!customElements.get('faq-overzicht')) {
  customElements.define('faq-overzicht', FaqOverzicht);
}